import * as React from 'react';

/**
 * Custom hook for managing file upload state and operations
 *
 * @param setUploadErrorText - Function to set upload error messages
 * @returns Object containing file state and operations
 */
export function useFileUpload(setUploadErrorText: (error: string) => void) {
  const [selectedFiles, setSelectedFiles] = React.useState<File[]>([]);

  /**
   * Handles adding new files to the selection
   * Clears any existing upload errors
   *
   * @param files - Array of files to add
   */
  const handleFileChange = React.useCallback(
    (files: File[]) => {
      setSelectedFiles((prev) => [...prev, ...files]);
      setUploadErrorText('');
    },
    [setUploadErrorText]
  );

  /**
   * Removes a file at the specified index
   *
   * @param index - Index of the file to remove
   */
  const handleRemoveFile = React.useCallback((index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  /**
   * Clears all selected files and upload errors
   */
  const handleClearFiles = React.useCallback(() => {
    setSelectedFiles([]);
    setUploadErrorText('');
  }, [setUploadErrorText]);

  /**
   * Clears all selected files (used during form reset)
   */
  const clearFiles = React.useCallback(() => {
    setSelectedFiles([]);
  }, []);

  return {
    clearFiles,
    handleClearFiles,
    handleFileChange,
    handleRemoveFile,
    selectedFiles,
    setSelectedFiles,
  };
}
